// Vender SIN INTERNET (parte 2). Si no hay señal al cobrar, la venta se guarda
// en este dispositivo y se sube sola cuando vuelve la conexión.
// - Cada venta lleva su número de ticket creado aquí: si se envía dos veces,
//   la base la registra una sola vez (sql/092).
// - Se registra aunque el stock quede en negativo (la venta ya ocurrió); al
//   subir, la app avisa qué productos quedaron así para revisarlos.
import { supabase } from './supabaseClient';
import { registrarComisionBancaria } from './gastosService';

const clave = (negocioId) => `kontrol-ventas-pendientes-${negocioId}`;
const avisar = () => window.dispatchEvent(new Event('kontrol-pendientes'));

export function nuevoTicket() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ¿El error es por falta de internet (y no un rechazo de la base)?
export function esErrorDeRed(e) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const m = String(e?.message || e || '');
  return !e?.code && /failed to fetch|networkerror|load failed|network request failed|fetch failed/i.test(m);
}

export function listarPendientes(negocioId) {
  if (!negocioId) return [];
  try { return JSON.parse(localStorage.getItem(clave(negocioId)) || '[]'); } catch { return []; }
}

function guardarLista(negocioId, lista) {
  try {
    if (lista.length) localStorage.setItem(clave(negocioId), JSON.stringify(lista));
    else localStorage.removeItem(clave(negocioId));
  } catch { /* sin almacenamiento */ }
  avisar();
}

// venta: { ticket, args (parámetros de registrar_venta_carrito), comision, resumen, userId }
export function guardarVentaPendiente(negocioId, venta) {
  const lista = listarPendientes(negocioId);
  lista.push({ ...venta, creada: new Date().toISOString(), error: null });
  guardarLista(negocioId, lista);
}

export function descartarPendiente(negocioId, ticket) {
  guardarLista(negocioId, listarPendientes(negocioId).filter((v) => v.ticket !== ticket));
}

// Cantidades vendidas sin subir, por producto (para mostrar el stock real).
export function ajusteStockPendiente(negocioId) {
  const ajuste = {};
  for (const v of listarPendientes(negocioId)) {
    for (const it of v.args?.p_items || []) {
      ajuste[it.producto_id] = (ajuste[it.producto_id] || 0) + Number(it.cantidad || 0);
    }
  }
  return ajuste;
}

let subiendo = null;

// Sube las ventas pendientes de esta persona. Devuelve
// { subidas, conError, negativos: [nombres de productos con stock < 0] }.
export function subirPendientes(negocioId, userId) {
  if (!subiendo) subiendo = subir(negocioId, userId).finally(() => { subiendo = null; });
  return subiendo;
}

async function subir(negocioId, userId) {
  const resultado = { subidas: 0, conError: 0, negativos: [] };
  const vendidos = new Set();
  for (const venta of listarPendientes(negocioId)) {
    if (venta.userId && venta.userId !== userId) continue;
    const { error } = await supabase.rpc('registrar_venta_carrito', {
      ...venta.args, p_ticket: venta.ticket, p_sin_conexion: true,
    });
    if (error) {
      if (esErrorDeRed(error)) break; // sigue sin señal: se intenta luego
      guardarLista(negocioId, listarPendientes(negocioId).map((v) => (v.ticket === venta.ticket
        ? { ...v, error: error.message || 'No se pudo registrar' } : v)));
      resultado.conError += 1;
      continue;
    }
    descartarPendiente(negocioId, venta.ticket);
    resultado.subidas += 1;
    (venta.args.p_items || []).forEach((it) => vendidos.add(it.producto_id));
    if (venta.comision) {
      try { await registrarComisionBancaria(venta.comision); } catch { resultado.comisionFallida = true; }
    }
  }
  if (vendidos.size) {
    const { data } = await supabase.from('productos').select('nombre, stock')
      .in('id', [...vendidos]).lt('stock', 0);
    resultado.negativos = (data || []).map((p) => `${p.nombre} (${Number(p.stock)})`);
  }
  return resultado;
}
